import { formatMoney } from '@theme/money-formatting';

(() => {
  const PICKER = 'variant-picker';
  const ADJUSTMENT_OPTION = /clasp|metal|bail|fitting|gift packaging/i;

  function readVariants(picker) {
    try {
      return JSON.parse(picker.querySelector('[data-ora-variants]')?.textContent || '[]');
    } catch (error) {
      console.warn('Ora variant price data could not be read.', error);
      return [];
    }
  }

  function selectedOptions(picker) {
    return Array.from(picker.querySelectorAll('.variant-option')).map((option) => {
      const checked = option.querySelector('input:checked');
      const select = option.querySelector('select');
      return checked?.value ?? select?.value ?? '';
    });
  }

  function exactVariant(variants, options) {
    return variants.find(
      (variant) =>
        variant.options.length === options.length &&
        variant.options.every((value, index) => value === options[index])
    );
  }

  function formattedAdjustment(picker, amount) {
    const format = picker.dataset.oraMoneyFormat || '${{amount}}';
    const currency = picker.dataset.oraCurrency || 'AUD';
    return formatMoney(amount, format, currency);
  }

  function updateLabel(label, variant, displayPrice) {
    const price = label.querySelector('.ora-variant-price');
    const compare = label.querySelector('.ora-variant-compare');
    const status = label.querySelector('.ora-variant-status');
    if (!price || !compare || !status) return;

    price.textContent = displayPrice ?? variant?.price ?? '';
    compare.textContent = variant?.compareAtPrice || '';
    compare.hidden = !variant?.compareAtPrice;

    const unavailable = !variant;
    const soldOut = Boolean(variant && !variant.available);
    status.textContent = unavailable ? 'Unavailable' : soldOut ? 'Sold out' : '';
    status.hidden = !(unavailable || soldOut);
    label.classList.toggle('ora-option-unavailable', unavailable);
    label.classList.toggle('ora-option-sold-out', soldOut);
    label.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
  }

  function updatePicker(picker) {
    const variants = readVariants(picker);
    if (!variants.length) return;
    const current = selectedOptions(picker);

    picker.querySelectorAll('[data-ora-option-index]').forEach((label) => {
      const optionIndex = Number(label.dataset.oraOptionIndex);
      const hypothetical = [...current];
      hypothetical[optionIndex] = label.dataset.oraOptionValue || '';
      const variant = exactVariant(variants, hypothetical);
      const fieldset = label.closest('[data-ora-option-name]');
      const isAdjustment = ADJUSTMENT_OPTION.test(fieldset?.dataset.oraOptionName || '');
      let displayPrice;

      if (isAdjustment && variant) {
        const optionVariants = Array.from(fieldset?.querySelectorAll('[data-ora-option-index]') || [])
          .map((optionLabel) => {
            const optionCombination = [...current];
            optionCombination[optionIndex] = optionLabel.dataset.oraOptionValue || '';
            return exactVariant(variants, optionCombination);
          })
          .filter(Boolean);
        const baselineVariant = optionVariants.reduce(
          (lowest, candidate) => (!lowest || candidate.priceAmount < lowest.priceAmount ? candidate : lowest),
          null
        );

        if (baselineVariant) {
          const adjustment = variant.priceAmount - baselineVariant.priceAmount;
          displayPrice = adjustment > 0 ? `+${formattedAdjustment(picker, adjustment)}` : 'Included';
        }
      }

      updateLabel(label, variant, displayPrice);
    });
  }

  function updateAll(root = document) {
    root.querySelectorAll?.(PICKER).forEach(updatePicker);
  }

  document.addEventListener('DOMContentLoaded', () => updateAll());
  document.addEventListener('change', (event) => {
    const picker = event.target instanceof Element ? event.target.closest(PICKER) : null;
    if (picker) queueMicrotask(() => updatePicker(picker));
  });
  document.addEventListener('variant:update', (event) => {
    const picker = event.target instanceof Element ? event.target.closest(PICKER) : null;
    requestAnimationFrame(() => (picker ? updatePicker(picker) : updateAll(event.target)));
  });
  document.addEventListener('shopify:section:load', (event) => updateAll(event.target));
})();
